
























































































































/* General */

/* Mixins */
import EntityTypeMixin from '@/mixins/entity-type.mixin'

/* Components */
import QuickAddMemberPicker from '@/components/entity/quick-add-member-picker.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { DefaultFilterIDs, EEntities } from '@/types'
import {
  IGenericJSONMessage,
  IPersonBase,
  IProjectDailyActivityLogsDailyActivityLog,
  IProjectsOutputProjectForHome,
} from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'

export default EntityTypeMixin.extend({
  name: 'DashboardCardEntity',

  components: {
    QuickAddMemberPicker,
  },

  props: {
    entity: {
      type: Object as () => IProjectsOutputProjectForHome,
      required: true,
    },
    profile: {
      type: Object as () => IPersonBase,
      required: true,
    },
    dailyActivityLog: {
      type: Object as () => IProjectDailyActivityLogsDailyActivityLog,
      default: null,
    },
  },

  data() {
    return {
      RouteAssistant,
      isSubmitting: false,
      showMoreOptions: false,
      showRenameInput: false,
      updatedName: '',
      members: [] as IPersonBase[],
    }
  },

  computed: {
    hasDailyActivityLog(): boolean {
      return this.dailyActivityLog !== null
    },
    activeCountLabel(): string {
      if (this.entityType === EEntities.PROJECTS) {
        return this.entity.activeCount === 1 ? 'active task' : 'active tasks'
      } else if (this.entityType === EEntities.MAILBOXES) {
        return this.entity.activeCount === 1
          ? 'active conversation'
          : 'active conversations'
      }
    },
    setupCompleted(): boolean {
      return this.entity.setupStatus === 'complete'
    },
    showSetupButton(): boolean {
      return (
        !this.setupCompleted &&
        !this.$screen.isMobile(this.$mq) &&
        this.$permissions.isAdminOrOwner
      )
    },
    finishSetupText(): string {
      if (this.setupCompleted) {
        return ''
      }
      if (this.entity.setupStatus === 'forwarding incomplete') {
        return 'Finish Auto-Forwarding Setup'
      } else if (this.entity.setupStatus === 'from incomplete') {
        return 'Finish Outgoing Email Setup'
      } else {
        return 'Set up this Mailbox'
      }
    },
  },

  watch: {
    entity: {
      immediate: true,
      handler(value) {
        this.members = value.listMembers
      },
    },
  },

  methods: {
    async updateName() {
      try {
        if (
          this.updatedName.trim() === '' ||
          this.updatedName.trim() === this.entity.name
        ) {
          this.showRenameInput = false
          return
        }

        if (this.isSubmitting) {
          return
        }

        this.isSubmitting = true

        let response: IGenericJSONMessage = null

        if (this.entityType === EEntities.PROJECTS) {
          response = await InternalProjectsAPI.put_Title(
            this.entity.id,
            this.updatedName,
          )
        } else if (this.entityType === EEntities.MAILBOXES) {
          response = await MailboxesAPI.put_Title(
            this.entity.id,
            this.updatedName,
          )
        }

        this.entity.name = this.updatedName
        this.showRenameInput = false
        this.$messaging.showSuccess(response.message)
        this.updatedName = ''
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
      }
    },
    async archive() {
      try {
        this.closeOptions()
        let response: IGenericJSONMessage = null
        if (this.entityType === EEntities.PROJECTS) {
          response = await InternalProjectsAPI.post_Archive(this.entity.id)
        } else if (this.entityType === EEntities.MAILBOXES) {
          response = await MailboxesAPI.post_Archive(this.entity.id)
        }
        this.$messaging.showSuccess(response.message)
        this.$emit('entity-archived', this.entity)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
      }
    },
    async onMembersAdded() {
      if (this.entityType === EEntities.PROJECTS) {
        this.members = await InternalProjectsAPI.get_Members(this.entity.id)
      } else if (this.entityType === EEntities.MAILBOXES) {
        this.members = await MailboxesAPI.get_Members(this.entity.id)
      }
    },
    enableRenameInput() {
      this.closeOptions()
      this.$nextTick(() => {
        // @ts-ignore
        this.$refs.renameRef.focus()
      })
      this.showRenameInput = true
      this.updatedName = ''
    },
    disableRenameInput() {
      this.showRenameInput = false
    },
    closeOptions() {
      this.showMoreOptions = true
      this.$nextTick(() => {
        this.showMoreOptions = false
      })
    },
    limitDigits(value: number) {
      return value < 1000 ? value : '999+'
    },
    getPlotPosition(index: number): string {
      return (
        '--y: ' +
        this.getYPosition(index) +
        'px; --x: ' +
        this.getXPosition(index) +
        'px;'
      )
    },
    getYPosition(index: number): number {
      const bottomPad = 10
      const maxHeight = 40 - bottomPad
      const curValue = this.dailyActivityLog.listDailyCounts[index]
      const maxValue = Math.max.apply(
        Math,
        this.dailyActivityLog.listDailyCounts,
      )
      return curValue === 0
        ? bottomPad
        : bottomPad + (curValue / maxValue) * maxHeight
    },
    getXPosition(index: number): number {
      const leftPad = 0
      return leftPad + index * 6
    },
    getLengthAndAngle(index: number): string {
      if (index === this.dailyActivityLog.listDailyCounts.length - 1) {
        return ''
      }

      const y = this.getYPosition(index + 1) - this.getYPosition(index)
      const x = this.getXPosition(index + 1) - this.getXPosition(index)
      const opp = this.getYPosition(index) - this.getYPosition(index + 1)
      const hyp = Math.sqrt(Math.pow(y, 2) + Math.pow(x, 2))
      const angle = Math.asin(opp / hyp) * (180 / Math.PI)

      return '--length: ' + hyp + '; --angle: ' + angle
    },
    handleFavoriteClick() {
      this.$emit('favorite-clicked')
    },
    goToNextSetupStep() {
      if (this.entity.setupStatus === 'from incomplete') {
        RouteAssistant.goToMailboxSettingsOutgoingEmailRoute(
          this.$router,
          this.$route.params.accountId,
          this.entity.id,
        )
      } else {
        RouteAssistant.goToMailboxVerificationIntroRoute(
          this.$router,
          this.$route.params.accountId,
          this.entity.id,
        )
      }
    },
    goToHome() {
      switch (this.entityType) {
        case EEntities.MAILBOXES:
          return RouteAssistant.goToMailbox(
            this.$router,
            this.$route.params.accountId,
            this.entity.id,
            this.entity.name,
          )
        case EEntities.PROJECTS:
          return RouteAssistant.goToProjectLanding(
            this.$router,
            this.$route.params.accountId,
            this.entity.id,
            this.$permissions,
            this.entity.name,
          )
      }
    },
    getActiveRoute() {
      switch (this.entityType) {
        case EEntities.MAILBOXES:
          return RouteAssistant.getMailboxRoute(
            this.$route.params.accountId,
            this.entity.id,
            this.entity.name,
            DefaultFilterIDs.ACTIVE,
          )
        case EEntities.PROJECTS:
          return RouteAssistant.getProjectLandingRoute(
            this.$route.params.accountId,
            this.entity.id,
            this.$permissions,
            this.entity.name,
            DefaultFilterIDs.ACTIVE,
          )
      }
    },
    getAssignedToYouRoute() {
      switch (this.entityType) {
        case EEntities.MAILBOXES:
          return RouteAssistant.getMailboxRoute(
            this.$route.params.accountId,
            this.entity.id,
            this.entity.name,
            DefaultFilterIDs.ASSIGNED_TO_YOU,
          )
        case EEntities.PROJECTS:
          return RouteAssistant.getProjectLandingRoute(
            this.$route.params.accountId,
            this.entity.id,
            this.$permissions,
            this.entity.name,
            DefaultFilterIDs.ASSIGNED_TO_YOU,
          )
      }
    },
  },
})
